import 'simplebar/dist/simplebar.min.css';
import React, { useRef, useState } from 'react';
import Checkmark from '../../icons/Checkmark';
import SimpleBar from 'simplebar-react';
import TextField from './TextField';
import classes from '../../styles/Select.module.scss';
import cx from 'classnames';

interface OptionProps {
  // value of select instance
  instance: string;
  // whether option is currently selected, false by default
  isSelected: boolean;
  // label of option
  label: string;
  // option icon, can be string (reference utils/iconStrings.tsx for mapping),
  // or passed in icon component, null by default
  // icon?: string | JSX.Element | null;
  icon?: (selected: boolean) => JSX.Element;
  // whether option is currently hovered, false by default
  hovered: boolean;
  // on mouse enter handler
  handleHover:
    | ((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void)
    | undefined;
  // on click handler
  handleClick: Function;
}

// custom option component
function SelectOption({
  instance,
  isSelected,
  icon,
  label,
  hovered,
  handleHover,
  handleClick,
}: OptionProps) {
  const Icon = icon ? icon(isSelected) : null;

  const renderLabel = Icon ? (
    <span className={classes.optionIconLabel}>
      <div>{Icon}</div>
      {label}
    </span>
  ) : (
    label
  );

  return (
    <div
      role="option"
      data-instance={instance}
      aria-selected={isSelected}
      className={cx(classes.selectOption, {
        [classes.hovered]: hovered,
        [classes.selected]: isSelected,
      })}
      tabIndex={0}
      onMouseEnter={handleHover}
      onMouseDown={(e: any) => {
        handleClick();
        e.stopPropagation();
      }}
      onKeyPress={() => null}
    >
      {renderLabel}
      {isSelected && <Checkmark width={16} />}
    </div>
  );
}

// options can be array of string or number primitives, with single label/value
type OptionPrimitive = string | number;
// options can be array of objects with discreet label/value as well as custom icon
// if options are to have custom icons, they must be objects
interface OptionObject {
  value: OptionPrimitive;
  label: string;
  icon?: (selected: boolean) => JSX.Element;
}
type SelectOptionType = OptionObject | OptionPrimitive;

// Both selected & onSelect are required as props because anytime we want
// to use a select component we will want to be able to tie into/control what the selected value is
interface SelectProps {
  // whether to show textual or contained, contained by default
  textual?: boolean;
  // style will be applied to the main select element (div)
  style?: any;
  // className will be applied to the main select element (div)
  className?: string;
  // label to display above select
  label?: string;
  // placeholder text when nothing is selected, 'Select' by default
  placeholder?: string;
  // An array of string options to display in the dropdown
  options: SelectOptionType[];
  // default option icon, can be string (reference utils/iconStrings.tsx for mapping),
  // or passed in icon component, null by default
  // optionIcon?: string | JSX.Element | null;
  optionIcon?: (selected: boolean) => JSX.Element;
  // whether select should be searchable, false by default
  searchable?: boolean;
  // placeholder for search input, 'Search' by default
  searchPlaceholder?: string;
  // selected option, can be object or primitive depending on options format, null if no selection
  selected: SelectOptionType | null;
  // function to call with option when selected
  onSelect: Function;
  // whether dropdown should close on select, true by default
  closeOnSelect?: boolean;
  // custom dropdown max height, 300px by default
  dropdownMaxHeight?: number;
  // whether select is disabled in UI, false by default
  disable?: boolean;
}

export default function Select({
  textual = false,
  style,
  className,
  label,
  options,
  optionIcon,
  searchable = false,
  searchPlaceholder = 'Search',
  placeholder = 'Select',
  selected,
  onSelect,
  closeOnSelect = true,
  dropdownMaxHeight = 300,
  disable = false,
}: SelectProps) {
  // // // component state: // // //
  // give each instance a unique identifier for managing blur event logic
  const [instance] = useState(() => Math.random().toString());
  // is select dropdown open
  const [open, setOpen]: [boolean, Function] = useState(() => false);
  // manage active row on mouse hover/arrow keypress
  const [activeRow, setActiveRow]: [number | null, Function] = useState(
    () => null
  );
  // search term if select is searchable
  const [searchTerm, setSearchTerm] = useState(() => '');

  // // // refs: // // //
  // ref for programatic blurring
  const selectRef: any = useRef(null);

  // // // event handler functions: // // //
  function handleToggleOpen(e: any) {
    // don't open if disabled
    if (disable) return;
    // prevent select from closing when clicking in/around search input
    if (
      searchable &&
      e.target &&
      e.target.attributes &&
      e.target.attributes.class
    ) {
      if (
        e.target.attributes.class.nodeValue.includes('TextField') ||
        e.target.attributes.class.nodeValue.includes('simplebar-content')
      ) {
        return;
      }
    }

    const isOpen = open;
    setOpen(!isOpen);
    if (isOpen) {
      setActiveRow(null);
      setSearchTerm('');
    }
  }
  // update search term
  // TODO: debounce
  function handleSearchInput(e: any) {
    const input = e.target.value;
    setSearchTerm(input);
  }
  // select option and, unless prevented, close and blur select
  function handleSelect(option: SelectOptionType) {
    onSelect(option);
    if (closeOnSelect) {
      setOpen(false);
      selectRef.current.blur();
    }
  }
  // blur select unless event is triggered by clicking within own bounds
  function handleBlurSelect(e: any) {
    const target = e.relatedTarget || document.activeElement;

    if (
      target &&
      target.attributes &&
      target.attributes.class &&
      target.attributes.class.nodeValue.includes('TextField')
    ) {
      return;
    }
    if (
      target === null ||
      !target.attributes ||
      !target.attributes['data-instance'] ||
      target.attributes['data-instance'].value !== instance
    ) {
      setOpen(false);
      setActiveRow(null);
    }
  }
  // allow browse and select options via keyboard input
  function handleKeyPress(e: any) {
    switch (e.key) {
      case 'ArrowUp':
        if (activeRow === null) return setActiveRow(options.length - 1);
        if (activeRow > 0) return setActiveRow(activeRow - 1);
        break;
      case 'ArrowDown':
        if (activeRow === null) return setActiveRow(0);
        if (activeRow < options.length - 1) return setActiveRow(activeRow + 1);
        break;
      case 'Enter':
        if (typeof activeRow === 'number')
          return handleSelect(options[activeRow]);
        return setOpen(!open);
      default:
        break;
    }
    return null;
  }

  // // // render variables: // // //
  // text to display in select
  const selectText =
    (selected instanceof Object && selected.label
      ? selected.label
      : selected) || placeholder;
  // value of selected option
  const selectedValue =
    selected instanceof Object && selected.value ? selected.value : selected;

  // // // render functions: // // //
  // renders select dropdown with options
  function renderDropdown(render: boolean) {
    if (!render) return null;

    return (
      <div className={classes.selectDropdown}>
        <SimpleBar style={{ maxHeight: dropdownMaxHeight }}>
          {searchable && (
            <TextField
              style={{
                width: '96%',
                margin: 'auto',
                marginBottom: '8px',
                marginTop: '-4px',
              }}
              placeholder={searchPlaceholder}
              value={searchTerm}
              onFocus={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onChange={handleSearchInput}
              iconType="searchIcon"
            />
          )}
          {options.reduce((acc: any[], opt: SelectOptionType, i: number) => {
            const optLabel: string =
              opt instanceof Object && opt.label
                ? opt.label
                : typeof opt === 'string'
                ? opt
                : 'undefined';

            if (searchable) {
              // if option label does not contain search term, do not include
              if (!optLabel.toLowerCase().includes(searchTerm.toLowerCase())) {
                return acc;
              }
            }

            const optValue =
              opt instanceof Object && opt.value ? opt.value : opt;
            const optIcon =
              opt instanceof Object && opt.icon ? opt.icon : optionIcon;
            const isSelected = selectedValue === optValue;
            const hovered = i === activeRow;

            acc.push(
              <SelectOption
                instance={instance}
                key={JSON.stringify(opt)}
                icon={optIcon}
                label={optLabel}
                isSelected={isSelected}
                hovered={hovered}
                handleHover={() => (activeRow !== i ? setActiveRow(i) : null)}
                handleClick={() => handleSelect(opt)}
              />
            );

            return acc;
          }, [])}
        </SimpleBar>
      </div>
    );
  }

  return (
    <div
      className={cx(
        classes.selectRoot,
        { [classes.textual]: textual },
        className
      )}
      style={{ ...style }}
    >
      {label && <p className={classes.selectLabel}>{label}</p>}
      <div
        role="button"
        data-instance={instance}
        ref={selectRef}
        className={cx(classes.selectBody, {
          [classes.textualSelect]: textual,
          [classes.disable]: disable,
          [classes.openSelect]: open,
        })}
        tabIndex={0}
        onClick={handleToggleOpen}
        onBlur={handleBlurSelect}
        onKeyDown={handleKeyPress}
      >
        <span className={classes.selectText}>{selectText}</span>
        {renderDropdown(open)}
        <span
          className={cx(classes.selectArrow, {
            [classes.hideDivider]: textual,
            [classes.openArrow]: open,
          })}
        >
          <i />
        </span>
      </div>
    </div>
  );
}
